import React, { useEffect, useState } from "react";
import {
  CheckBoxOutlineBlankOutlined,
  CheckBoxOutlined,
  FileDownloadOutlined,
} from "@mui/icons-material";
import {
  Checkbox,
  FormControlLabel,
  IconButton,
  Tooltip,
  Box,
} from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { toast } from "react-toastify";
import moment from "moment";

import AppConfirmDialog from "components/Shared/AppConfirmDialog";
import AppForm from "components/Shared/AppForm";
import BaseAutocomplete from "components/Shared/Base/BaseAutocomplete";
import DateRangeToggle from "components/Shared/DateRangeToggle";

import { useLazyListCODChargeDeductReportQuery } from "store/services/reportApi";
import { useLazyGetDDLVendorListReportQuery } from "store/services/reportApi";
import { useAuth } from "hooks";

import * as XLSX from "xlsx";
import { localizeDateShort } from "utils/dateFormats";

const INITIAL_FORM = {
  dateRange: [],
  vendor: null,
};

const ExportCODChargeDeductReport = ({ isBusy = false }) => {
  const [open, setOpen] = useState(false);
  const [showVendorFilter, setShowVendorFilter] = useState(false);

  const { viewEntity } = useAuth();

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = useForm({ defaultValues: INITIAL_FORM });

  // RTK Queries
  const [fetchReport, { isLoading: downloading }] =
    useLazyListCODChargeDeductReportQuery();

  const [fetchVendors, { data: vendorData, isLoading: loadingVendors }] =
    useLazyGetDDLVendorListReportQuery();

  // Load vendors only when dialog opens
  useEffect(() => {
    if (!open) return;
    fetchVendors({
      account: viewEntity?.entity?.account?._id,
      entity: viewEntity?.entity?._id,
      status: "Active",
    });
  }, [open, fetchVendors, viewEntity]);

  const vendorOptions = vendorData?.data || [];

  const onDownload = async (form) => {
    const { dateRange, vendor } = form;

    if (!dateRange?.[0] || !dateRange?.[1]) {
      toast.error("Please select a valid date range.");
      return;
    }

    const params = {
      account: viewEntity?.entity?.account?._id,
      entity: viewEntity?.entity?._id,
      page: 1,
      limit: 10000,
      fromDate: moment(dateRange[0]).format("MM-DD-YYYY"),
      toDate: moment(dateRange[1]).format("MM-DD-YYYY"),
      ...(showVendorFilter && vendor && { vendorId: vendor.value }),
    };

    try {
      const response = await fetchReport(params);

      if (response.error) {
        toast.error("Failed to fetch COD service charge deduct report data.");
        console.error("API Error:", response.error);
        return;
      }

      const data = response.data?.data || response.data || [];

      if (!data || data.length === 0) {
        toast.info("No records found for selected filters.");
        return;
      }

      const headers = [
        "COD Transfer ID",
        "Shipment IDs",
        "Vendor",
        "Actual COD Amount",
        "Service Charge (1%)",
        "Delivery Charge",
        "Transfer Amount",
        "Transfer Date",
      ];

      const rows = data.map((row) => [
        row.codTransferId || "N/A",
        Array.isArray(row?.shipmentId) && row.shipmentId.length > 0
          ? row.shipmentId.join(", ")
          : row?.shipmentId || "N/A",
        row?.vendorShopName || "",
        Number(row?.actualAmount || 0),
        Number(row?.feeAmount || 0),
        Number(row?.deductChargeFromCOD || 0),
        Number(row?.transferAmount || 0),
        row?.transferDate ? localizeDateShort(row.transferDate) : "N/A",
      ]);

      const totalRow = rows.length + 2;
      rows.push(["", "", "Total", null, null, null, null, ""]);

      const worksheet = XLSX.utils.aoa_to_sheet([headers, ...rows]);
      const range = XLSX.utils.decode_range(worksheet["!ref"]);

      const amountCols = [3, 4, 5, 6];
      for (let row = 1; row <= range.e.r; row += 1) {
        amountCols.forEach((c) => {
          const cell = XLSX.utils.encode_cell({ r: row, c });
          if (worksheet[cell]) worksheet[cell].z = "#,##0.00";
        });
      }

      amountCols.forEach((c) => {
        const colLetter = XLSX.utils.encode_col(c);
        worksheet[`${colLetter}${totalRow}`] = {
          t: "n",
          f: `SUM(${colLetter}2:${colLetter}${totalRow - 1})`,
          z: "#,##0.00",
        };
      });

      worksheet["!cols"] = [
        { wch: 18 },
        { wch: 24 },
        { wch: 20 },
        { wch: 18 },
        { wch: 18 },
        { wch: 18 },
        { wch: 18 },
        { wch: 18 },
      ];
      worksheet["!autofilter"] = { ref: `A1:H${totalRow - 1}` };

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "COD Charge Deduct");
      XLSX.writeFile(
        workbook,
        `COD_Charge_Deduct_Report_${moment().format("YYYY-MM-DD")}.xlsx`,
      );

      const totalItems = response.data?.totalItems || data.length;
      if (totalItems > data.length) {
        toast.info(`Downloaded ${data.length} of ${totalItems} records.`);
      } else {
        toast.success(
          `Excel downloaded successfully! ${data.length} records exported.`
        );
      }
    } catch (err) {
      console.error("Export error:", err);
      toast.error("Failed to download data. Please try again.");
    } finally {
      onClose();
    }
  };

  const onClose = () => {
    setOpen(false);
    setShowVendorFilter(false);
    reset(INITIAL_FORM);
  };

  return (
    <>
      <Tooltip title="Download Excel" arrow>
        <IconButton color="success" onClick={() => setOpen(true)}>
          <FileDownloadOutlined />
        </IconButton>
      </Tooltip>

      <AppConfirmDialog
        open={open}
        onClose={onClose}
        onConfirm={handleSubmit(onDownload)}
        title="Download COD Service Charge Deduct Report"
        loading={downloading || isBusy}
        dialogIcon={() => <FileDownloadOutlined color="success" />}
        sx={{ "& .MuiDialog-paper": { maxWidth: 560, width: "100%" } }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, p: 1 }}>
          {/* Date Range */}
          <Controller
            name="dateRange"
            control={control}
            rules={{ required: "Date range is required" }}
            render={({ field, fieldState }) => (
              <DateRangeToggle
                {...field}
                label="Select Date Range"
                size="small"
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
              />
            )}
          />

          {/* Filter by Vendor */}
          <FormControlLabel
            control={
              <Checkbox
                checked={showVendorFilter}
                onChange={(e) => {
                  setShowVendorFilter(e.target.checked);
                  if (!e.target.checked) {
                    setValue("vendor", null);
                  }
                }}
                icon={<CheckBoxOutlineBlankOutlined />}
                checkedIcon={<CheckBoxOutlined />}
                size="small"
              />
            }
            label="Filter by Vendor"
            sx={{ m: 0 }}
          />

          {showVendorFilter && (
            <AppForm loading={isBusy}>
              <AppForm.Content>
                <Controller
                  name="vendor"
                  control={control}
                  rules={{
                    required: showVendorFilter ? "Select a vendor" : false,
                  }}
                  render={({ field }) => (
                    <BaseAutocomplete
                      fullWidth
                      label="Vendor"
                      required={showVendorFilter}
                      value={field.value || null}
                      onChange={(_, v) => field.onChange(v)}
                      options={vendorOptions}
                      getOptionLabel={(o) => o?.label || ""}
                      isOptionEqualToValue={(o, v) => o?.value === v?.value}
                      loading={loadingVendors}
                      loadingText="Loading vendors..."
                      helperText={errors.vendor?.message}
                      error={!!errors.vendor}
                      size="small"
                    />
                  )}
                />
              </AppForm.Content>
            </AppForm>
          )}
        </Box>
      </AppConfirmDialog>
    </>
  );
};

export default ExportCODChargeDeductReport;
